import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

const CORE_VALUES = [
  { num: '01', title: 'Sisterhood, not competition', desc: 'We rise together, never at each other\'s expense.' },
  { num: '02', title: 'Wisdom, shared', desc: 'Your knowledge lifts thousands. What you know matters.' },
  { num: '03', title: 'Every story matters', desc: 'From every corner of the world, every voice counts equally.' },
  { num: '04', title: 'Authenticity above all', desc: 'Real experiences, honest lessons, genuine growth.' },
  { num: '05', title: 'Growth is non-linear', desc: 'We celebrate every step — especially the difficult ones.' },
  { num: '06', title: 'Dignity as our foundation', desc: 'Every woman is met with respect, warmth and grace.' }
];

const Community = () => {
  return (
    <div className="bg-ivory min-h-screen" data-testid="community-page">
      {/* Hero */}
      <section className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center space-y-8">
          <p className="label-editorial">About Behenchara</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl leading-[0.95] text-charcoal" data-testid="community-hero-title">
            A community<br/>built on <em className="text-burgundy not-italic">belief</em>.
          </h1>
          <p className="text-lg md:text-xl text-charcoal-muted max-w-2xl mx-auto leading-relaxed">
            That every woman deserves a place where she can speak, learn, ask questions, discover opportunities and find women who understand.
          </p>
        </div>
      </section>

      {/* Vision - Editorial Layout */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <p className="label-editorial mb-6">Our Philosophy</p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-charcoal">
              Sisters<br/>empowering<br/>sisters.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <p className="dropcap text-lg leading-relaxed text-charcoal-muted">
              Behenchara is not another platform. It is a movement — built on the quiet, radical belief that when women share what they know, everyone rises. We reject the notion that women must compete for limited seats at limited tables. Instead, we build new rooms.
            </p>
            <p className="text-lg leading-relaxed text-charcoal-muted">
              Here, a student in Delhi can learn from a founder in Lagos. A working mother in São Paulo can find community with a returning professional in Toronto. A woman navigating grief can find another who has walked the path before her. This is what solidarity looks like when it goes digital.
            </p>
            <p className="text-lg leading-relaxed text-charcoal-muted">
              We are for the woman who has questions no one has answered. For the one whose wisdom deserves an audience. For the one who has been alone in her ambition. You are not alone. There is a sisterhood.
            </p>
          </div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-ivory-warm border-t border-subtle">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-editorial mb-4">What We Believe</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">Our core values.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-subtle">
            {CORE_VALUES.map((val) => (
              <div key={val.num} className="p-10 border border-subtle -m-px bg-white" data-testid={`value-${val.num}`}>
                <p className="font-serif text-6xl text-terracotta mb-6">{val.num}</p>
                <h3 className="font-serif text-2xl text-charcoal mb-3">{val.title}</h3>
                <p className="text-charcoal-muted leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do - Share, Teach, Learn */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-editorial mb-4">How We Rise</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">Share. Teach. Learn.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-0 border border-subtle">
            {[
              { title: 'Share', desc: 'Every experience — the triumphs, the struggles, the pivots, the lessons — has the power to change another sister\'s life.' },
              { title: 'Teach', desc: 'The skills you\'ve mastered, the knowledge you\'ve earned — share them with sisters who are exactly where you once were.' },
              { title: 'Learn', desc: 'Grow through the collective wisdom of thousands of women. Every article, every conversation makes you stronger.' }
            ].map((item, i) => (
              <div key={item.title} className="p-10 border border-subtle -m-px bg-white">
                <p className="font-serif text-5xl text-burgundy mb-4">0{i + 1}</p>
                <h3 className="font-serif text-3xl text-charcoal mb-4">{item.title}</h3>
                <p className="text-charcoal-muted leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Get Involved */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-charcoal text-ivory">
        <div className="max-w-4xl mx-auto text-center space-y-10">
          <p className="label-editorial text-terracotta">Get Involved</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight">
            The sisterhood begins<br/>when you <em className="text-terracotta not-italic">join</em>.
          </h2>
          <p className="text-lg text-ivory/70 max-w-2xl mx-auto">
            Read. Share. Ask. Contribute. Every action makes the community stronger.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 bg-ivory text-charcoal hover:bg-beige transition-colors font-medium text-sm uppercase tracking-widest" data-testid="community-join-button">
              Join Behenchara <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/submit-story" className="inline-flex items-center gap-2 px-8 py-4 border border-ivory text-ivory hover:bg-ivory hover:text-charcoal transition-colors font-medium text-sm uppercase tracking-widest">
              Share Your Story
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Community;
